import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";

export function SBLogo({ size = 20, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <span
        className="relative inline-flex items-center justify-center"
        style={{ width: size + 6, height: size + 6 }}
      >
        <MapPin size={size} className="text-[#FFC107]" strokeWidth={2.4} />
      </span>
      <span
        className="font-black tracking-tight"
        style={{ fontSize: size * 0.85, letterSpacing: "-0.02em" }}
      >
        SPOTBOUNTY
      </span>
    </span>
  );
}

export function WebHeader() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#080B0D]/85 border-b border-[#2A2F34]">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="header-logo">
          <SBLogo size={20} />
        </Link>
        <nav className="hidden lg:flex items-center gap-7 text-[13px] text-[#A1A1AA]">
          <Link to="/businesses" className="hover:text-white transition-colors">For Businesses</Link>
          <Link to="/scouts" className="hover:text-white transition-colors">For Scouts</Link>
          <Link to="/operators" className="hover:text-white transition-colors">For Operators</Link>
          <a href="/#how" className="hover:text-white transition-colors">How It Works</a>
          <Link to="/admin/login" data-testid="header-signin" className="hover:text-white transition-colors">Login</Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/admin/login" data-testid="header-signin-mobile" className="lg:hidden text-[13px] text-[#A1A1AA] hover:text-white transition-colors px-2 py-2">Login</Link>
          <Link
            to="/businesses"
            data-testid="header-cta"
            className="sb-btn-primary text-[12px]"
            style={{ padding: "10px 16px" }}
          >
            Get Vending
          </Link>
        </div>
      </div>
    </header>
  );
}

export function WebFooter() {
  return (
    <footer className="border-t border-[#2A2F34] bg-[#0B0E11]">
      <div className="max-w-7xl mx-auto px-6 py-14 grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
        <div>
          <SBLogo size={20} />
          <p className="text-[#A1A1AA] mt-4 max-w-xs text-[14px] leading-relaxed">
            Find a spot. Earn the bounty. A marketplace connecting scouts, independent vending operators, and businesses.
          </p>
        </div>
        {[
          { title: "Product", links: [
            { label: "For Scouts", to: "/scouts" },
            { label: "For Operators", to: "/operators" },
            { label: "For Businesses", to: "/businesses" },
            { label: "For Creators", to: "/creators" },
            { label: "Sell a location or route", to: "/sell" },
          ]},
          { title: "Company", links: [
            { label: "Contact", to: "/contact" },
            { label: "FAQ", to: "/faq" },
          ]},
          { title: "Resources", links: [
            { label: "Help Center", to: "/faq" },
          ]},
          { title: "Legal", links: [
            { label: "Terms", to: "/terms" },
            { label: "Privacy", to: "/privacy" },
          ]},
        ].map((col) => (
          <div key={col.title}>
            <div className="sb-eyebrow mb-3">{col.title}</div>
            <ul className="space-y-2 text-[13px] text-[#A1A1AA]">
              {col.links.map((l) => (
                <li key={l.label}><Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/ /g,'-')}`} className="hover:text-white transition-colors">{l.label}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-[#2A2F34]">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="sb-eyebrow">Find a spot. Earn the bounty.</div>
            <div className="text-[12px] text-[#52525b] mt-2">© 2026 SpotBounty. All rights reserved.</div>
          </div>
          <Link
            to="/scouts"
            data-testid="footer-cta"
            className="sb-btn-primary"
          >
            Join Early Access <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </footer>
  );
}

export default function WebLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#080B0D] text-white">
      <WebHeader />
      <main>{children}</main>
      <WebFooter />
    </div>
  );
}
